import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { KeyFigure, KeyFigureStrip } from './key-figures';

/** Drawn geometry for a figure's tiny trend, in a 96 by 28 viewBox. */
export interface KeyFigureSpark {
  kind: 'line' | 'bars';
  /** Line segments (a gap where a period has no figure). */
  segments: string[];
  dots: Array<{ x: number; y: number; latest: boolean }>;
  bars: Array<{ x: number; y: number; width: number; height: number; latest: boolean; negative: boolean }>;
  /** The zero line, when the figures cross zero. */
  zero: number | null;
  /** Text alternative, e.g. "Trend: Dec 2021 £1.3m, Dec 2022 £1.7m". */
  text: string;
}

const WIDTH = 96;
const HEIGHT = 28;
const PAD = 3;
let nextId = 0;

/** Builds the trend geometry, or null when fewer than two periods have a figure. */
export function keyFigureSpark(figure: KeyFigure): KeyFigureSpark | null {
  const points = figure.trend;
  const values = points.map(point => point.value).filter((value): value is number => value !== null);
  if (values.length < 2) return null;
  const text = 'Trend: ' + points.map(point => `${point.label} ${point.display}`).join(', ');
  const bars = figure.trendKind === 'bars';
  // Bars grow from zero; a line uses the figures' own range so small movements still show.
  let min = bars ? Math.min(0, ...values) : Math.min(...values);
  let max = bars ? Math.max(0, ...values) : Math.max(...values);
  if (min === max) { min -= 1; max += 1; }
  const y = (value: number) => PAD + (max - value) / (max - min) * (HEIGHT - PAD * 2);
  const zero = min < 0 && max > 0 ? y(0) : null;
  const latestIndex = points.length - 1;
  if (bars) {
    const gap = 3;
    const width = (WIDTH - gap * (points.length - 1)) / points.length;
    return {
      kind: 'bars', segments: [], dots: [], zero, text,
      bars: points.flatMap((point, index) => {
        if (point.value === null) return [];
        const top = y(Math.max(point.value, 0));
        const bottom = y(Math.min(point.value, 0));
        return [{
          x: index * (width + gap), y: top, width, height: Math.max(bottom - top, 1.5),
          latest: index === latestIndex, negative: point.value < 0,
        }];
      }),
    };
  }
  const step = (WIDTH - PAD * 2) / (points.length - 1);
  const segments: string[] = [];
  let current: string[] = [];
  const dots: KeyFigureSpark['dots'] = [];
  points.forEach((point, index) => {
    if (point.value === null) {
      if (current.length > 1) segments.push(current.join(' '));
      current = [];
      return;
    }
    const px = PAD + index * step;
    const py = y(point.value);
    current.push(`${current.length ? 'L' : 'M'}${px.toFixed(1)} ${py.toFixed(1)}`);
    if (index === latestIndex) dots.push({ x: px, y: py, latest: true });
  });
  if (current.length > 1) segments.push(current.join(' '));
  return { kind: 'line', segments, dots, bars: [], zero, text };
}

/**
 * "Key figures" strip for a skinned company profile: headline figures for the selected service, each with its period,
 * a tiny trend, the year-on-year change, one line on why it matters and an optional flag. Figures that the financial
 * report shows are buttons that emit (openReport) with the report tab to open; "View full financial report" emits the
 * strip's default tab. An optional size test (e.g. audit exemption limits) and an advisers row sit underneath.
 * Skins theme it with --profile-key-*.
 */
@Component({
  selector: 'company-profile-key-figures',
  templateUrl: './company-profile-key-figures.component.html',
  styleUrls: ['./company-profile-key-figures.component.css'],
  standalone: false,
})
export class CompanyProfileKeyFiguresComponent implements OnChanges {
  @Input() strip: KeyFigureStrip | null = null;
  @Input() heading = 'Key figures for this relief';
  @Input() reportLinkLabel = 'View full financial report';
  /** Whether the financial report can open. When false, figures are not buttons and the report link is hidden. */
  @Input() canOpenReport = true;
  /** The financial report tab to open. */
  @Output() openReport = new EventEmitter<string>();
  /** A figure's own action id, for figures with an action rather than a report tab. */
  @Output() figureAction = new EventEmitter<string>();

  readonly headingId = `profile-key-figures-${++nextId}`;
  sparks: Record<string, KeyFigureSpark | null> = {};

  ngOnChanges(): void {
    this.sparks = {};
    for (const figure of this.strip?.figures || []) this.sparks[figure.id] = keyFigureSpark(figure);
  }

  open(tab: string | null | undefined): void {
    if (tab && this.canOpenReport) this.openReport.emit(tab);
  }

  runAction(figure: KeyFigure): void {
    if (figure.action) this.figureAction.emit(figure.action.id);
  }

  tabLabel(tab: string | null | undefined): string {
    switch (tab) {
      case 'Profit and Loss Account': return 'P&L';
      case 'Balance Sheet': return 'balance sheet';
      case 'Asset Breakdown': return 'asset breakdown';
      case 'Other Financial Metrics': return 'other metrics';
      default: return String(tab || '');
    }
  }

  /** Meter fill as a percentage of a track that runs to twice the limit, so the limit sits at the middle. */
  meterFill(ratio: number | null): number {
    return ratio === null ? 0 : Math.min(ratio / 2, 1) * 100;
  }

  changeIcon(direction: string): string {
    return direction === 'up' ? 'fa-arrow-up-right' : direction === 'down' ? 'fa-arrow-down-right' : 'fa-arrow-right';
  }
}
